import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { DeacParamList } from '../../../typings/Params';
import { useAuth } from '../../../context/Auth';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import ActingBodiesCommandsDropdown from '../../../components/Dropdowns/ActingBodiesCommandDropdown';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import UserDropdown from '../../../components/deac/UserDropdown';
import ApplicationsStatusDropdown from '../../../components/deac/ApplicationsStatusDropdown';
import { ButtonTypes } from '../../../components/CustomButton';
import { ApplicationStatus } from '../../../schemas/deac';

interface Props {
    filters: DeacParamList['Applications'];
    setFilters: React.Dispatch<React.SetStateAction<DeacParamList['Applications']>>;
    textFilter: string;
    setTextFilter: (value: string) => void;
    buttonType?: ButtonTypes;
}

function Filter({ filters, setFilters, textFilter, setTextFilter, buttonType }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<DeacParamList, 'Applications'>>>();
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);

    const [actingBodyId, setActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyCommandId, setActingBodyCommandId] = useState<number>(filters.actingBodyId ?? 0);
    const [status, setStatus] = useState<ApplicationStatus | 'all'>(filters.status ?? 'all');
    const [userId, setUserId] = useState<number>(filters.userId ?? 0);

    const handleCleanFilter = () => {
        setTextFilter('');
        setActingBodyId(0);
        setActingBodyUnitId(0);
        setActingBodyCommandId(0);
        setStatus('all');
        setUserId(0);
    };

    const handleFilter = () => {
        navigation.setParams({
            ...filters,
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
            actingBodyCommandId: actingBodyCommandId !== 0 ? actingBodyCommandId : undefined,
            status: status !== 'all' ? status : undefined,
            userId: userId !== 0 ? userId : undefined,
        });

        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
            actingBodyCommandId: actingBodyCommandId !== 0 ? actingBodyCommandId : undefined,
            status: status !== 'all' ? status : undefined,
            userId: userId !== 0 ? userId : undefined,
        });
    };

    const getActingBodyId = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_applications) {
            return actingBodyId;
        }

        return authenticatedUser.actingBodyUnit.actingBodyId;
    };

    function getActingBodyUnitId() {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_applications ||
            authenticatedUser.permissions.deac_manage_acting_body_units_applications
        ) {
            return actingBodyUnitId;
        }
        return authenticatedUser.actingBodyUnitId;
    }

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <Filters type={buttonType} onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />

            {authenticatedUser.permissions.deac_manage_all_acting_bodies_applications &&
                <ActingBodiesDropdown
                    includeAllOption={true}
                    value={actingBodyId}
                    setValue={setActingBodyId}
                    zIndex={5}
                    labelStyle={styles.selectLabelStyle}
                    viewStyle={{ gap: 8 }}
                />
            }

            {(authenticatedUser.permissions.deac_manage_all_acting_bodies_applications ||
                authenticatedUser.permissions.deac_manage_acting_body_units_applications)
                && <ActingBodiesUnitsDropdown
                    includeAllOption={true}
                    value={actingBodyUnitId}
                    setValue={setActingBodyUnitId}
                    actingBodyId={getActingBodyId()}
                    zIndex={4}
                    labelStyle={styles.selectLabelStyle}
                    viewStyle={{ gap: 8 }}
                />
            }

            <ActingBodiesCommandsDropdown
                includeAllOption={true}
                value={actingBodyCommandId}
                setValue={setActingBodyCommandId}
                actingBodyId={getActingBodyId()}
                disabled={false}
                zIndex={3}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            <ApplicationsStatusDropdown
                setValue={setStatus}
                value={status}
                zIndex={2}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            <UserDropdown
                label={translate('agent')}
                actingBodyId={getActingBodyId()}
                actingBodyUnitId={getActingBodyUnitId()}
                setValue={setUserId}
                value={userId}
                zIndex={1}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

        </Filters>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    selectLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    }
}));

export default Filter;
